import { GenericLazyString } from '@eventbrite/i18n';
import { ILogInUserApiResponseProps } from '../../api/login';
import { ILookUpUserApiResponseProps } from '../../api/lookup';
import { ISignupApiResponseProps } from '../../api/signup';
import { ISocialLogInApiResponseProps } from '../../api/social';
import { ISocialSignupActionData } from '../../constants/interfaces';

interface ILookupActionProps {
    signInMethods?: string[];
}

export interface ILookUpConfigMethodsProps {
    /**
     * Triggered when the entered text is an actual email
     * and before the api call
     */
    attempt?: () => void;
    success?: {
        ['WITH_USER_ID']?: {
            /**
             * Trigered when the user is existent
             * and the login methods includes password
             */
            ['HAS_PASSWORD']?: () => void;
            ['SOFT_ACCOUNT']?: {
                /**
                 * WARNING: If this notification is added, the modal to
                 * send the activation email won't be shown and the
                 * email won't be triggered!!
                 * There is no default notification for this action.
                 * Whatever goes into `extraMessage` will be put in
                 * the notification section.
                 * If no `extraMessage` is passed but a `ctaMessage`
                 * and a `ctaAction` is, that will be in the notification
                 */
                getNotification?: () => INotificationConfigProps;
                /**
                 * Triggered when the user is existent
                 * but has no login methods available
                 */
                action?: () => void;
            };
            ['SOCIAL_ACCOUNT']?: {
                /**
                 * If set, this notification props will be shown in each
                 * social account message we are showing.
                 */
                getNotification?: (
                    params: ILookupActionProps,
                ) => INotificationConfigProps;
                /**
                 * Triggered when the user is existent
                 * and the login methods are social
                 */
                action?: (params: ILookupActionProps) => void;
            };
        };
        ['WITHOUT_USER_ID']?: {
            getNotification?: () => INotificationConfigProps;
            /**
             * Triggered when user is non existent
             */
            action?: () => void;
        };
        /**
         * Triggered when the lookup has finished successfuly
         */
        default?: (response: ILookUpUserApiResponseProps) => void;
        /**
         * Triggered when the lookup has finished successfuly
         * but a social method triggered the lookup
         */
        social?: () => void;
        /**
         * Triggered when the lookup has finished successfuly
         * but a signup triggered the lookup
         */
        signup?: () => void;
    };
    /**
     * Triggered when the lookup call failed
     *
     */
    error?: () => void;
}

export interface ILogInConfigMethodsProps {
    /**
     * Optional text to be placed in the submit button.
     */
    submitButtonText?: GenericLazyString;
    /**
     * Triggered before the api call
     */
    attempt?: () => void;
    /**
     * Triggered when the api call was successful
     */
    success?: (response?: ILogInUserApiResponseProps) => void;
    error?: {
        /**
         * Triggered when the api call was failed
         * and the user needs to enter captcha
         */
        ['CAPTCHA_REQUIRED']?: () => void;
        /**
         * Triggered when the api call was failed
         * and email is invalid
         */
        ['USER_PASSWORD_INVALID']?: () => void;
        /**
         * Triggered when the api call was failed
         * and account has restricted access
         */
        ['ACCESS_DENIED']?: {
            getNotification?: () => INotificationConfigProps;
            /**
             * Triggered when the api call was failed
             * and account has restricted access
             */
            action?: () => void;
        };
        ['USER_EMAIL_INVALID']?: {
            getNotification?: () => INotificationConfigProps;
            /**
             * Triggered when the api call was failed
             * and the email was invalid
             */
            action?: () => void;
        };
        ['USER_HAS_NO_PASSWORD']?: {
            /**
             * Used when the user has no password but has a social
             * account linked.
             */
            getNotification?: () => INotificationConfigProps;
            /**
             * Triggered when the api call was failed
             * and the user has no password
             */
            action?: () => void;
        };
        /**
         * Triggered when the api call was failed
         * and the user is forbidden
         */
        ['FORBIDDEN']?: () => void;
        /**
         * Triggered when the api call was failed
         * and the account is suspended
         */
        ['USER_ACCOUNT_SUSPENDED']?: () => void;
        /**
         * Triggered when the api call was failed
         * and the password needs to be updated
         */
        ['CREDENTIALS_MUST_CHANGE']?: () => void;
        /**
         * Triggered when the api call was failed
         * because RISK blocked user
         */
        ['VERIFICATION_REQUIRED']?: {
            /**
             * Triggered when the api call was failed
             * and user can't login
             */
            action?: () => void;
        };
        /**
         * Triggered when the api call was failed
         * and no other condition was met
         */
        ['DEFAULT_ERROR']?: () => void;
    };
}

export interface ISignupConfigMethodsProps {
    /**
     * Optional text to be placed in the submit button.
     */
    submitButtonText?: {
        lookup: GenericLazyString;
        signup: GenericLazyString;
    };
    /**
     * Triggered before the api call
     */
    attempt?: () => void;
    /**
     * Triggered when the api call was successful
     */
    success?: (response?: ISignupApiResponseProps) => void;
    error?: {
        HAS_PASSWORD: {
            /**
             * If no `extraMessage` is passed but a `ctaMessage`
             * and a `ctaAction` is, that will be in the notification
             */
            getNotification?: (
                props?: ILookUpUserApiResponseProps,
            ) => INotificationConfigProps;
        };
        default: () => void;
    };

    [x: string]: any;
}

export interface IPasswordlessConfigMethodsProps {
    loginAttempt?: {
        attempt?: () => {};
        success?: () => {};
        error?: {
            ACCESS_DENIED?: {
                getNotification?: (
                    props?: ILookUpUserApiResponseProps,
                ) => INotificationConfigProps;
            };
            UNKNOWN_TOKEN?: {
                getNotification?: (
                    props?: ILookUpUserApiResponseProps,
                ) => INotificationConfigProps;
            };
            default: () => {};
        };
    };
    sendEmail?: {
        error?: {
            INVALID: {
                /**
                 * If no `extraMessage` is passed but a `ctaMessage`
                 * and a `ctaAction` is, that will be in the notification
                 */
                getNotification?: (
                    props?: ILookUpUserApiResponseProps,
                ) => INotificationConfigProps;
            };
        };
    };
}

interface ISocialErrorProps {
    socialProvider: string;
    socialData: any;
    errorMessage: any;
    notificationMessage?: GenericLazyString;
}

export interface ISocialLoginConfigMethodsProps {
    /**
     * Triggered before the api call
     */
    attempt?: () => void;
    /**
     * Triggered when the api call was successful
     */
    success?: (response?: ISocialLogInApiResponseProps) => void;
    /**
     * Triggered when the api call failed
     */
    error?: {
        default: (error: any) => void;
        ['EXTERNAL_USER_WITHOUT_EMAIL']?: (props: ISocialErrorProps) => void;
        ['EXTERNAL_USER_EMAIL_ALREADY_USED']?: (
            props: ISocialErrorProps,
        ) => void;
        ['EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT']?: (
            props: ISocialErrorProps,
        ) => void;
    };

    apple?: {
        login?: {
            /**
             * Triggered before the api call
             */
            attempt?: () => void;
            /**
             * Triggered when the api call was successful
             */
            success?: (response?: ISocialLogInApiResponseProps) => void;
            /**
             * Triggered when the api call failed
             */
            error?: (error: any) => void;
        };
        socialLoginPage?: {
            submitButtonText?: GenericLazyString;
            onCloseModal?: (redirectUrl: string) => void;
            success?: (response?: ISocialLogInApiResponseProps) => void;
        };
    };

    /**
     * Triggered when there is a preexistent account and the social methods
     * needs to link that preexistent account.
     * SocialLookupPage and probably others around the apple flow are
     * triggering this
     */
    linking?: {
        loookup?: {
            /**
             * Triggered before the api call
             */
            attempt?: () => void;
            /**
             * Triggered when the api call was successful
             */
            success?: (response?: any) => void;
            /**
             * Triggered when the api call failed
             */
            error?: (error: any) => void;
        };
    };

    /**
     * Method used in facebook after the response data doesn't brings the email
     * Edge case
     */
    signup?: {
        attempt?: (formData: ISocialSignupActionData) => void;
        success?: (response: ISocialLogInApiResponseProps) => void;
        error?: (formData: ISocialSignupActionData, error?: any) => void;
    };
}

export interface IToSConfigMethodsProps {
    /**
     * Triggered when the ToS dialog is shown
     */
    showToS?: () => void;
    /**
     * Triggered when the ToS dialog is accepted by the signup flow
     */
    acceptToS?: (formData: any) => void;
    /**
     * Triggered when the ToS dialog is accepted by the social login flow
     */
    acceptSocialToS?: () => void;
    /**
     * Triggered when the ToS dialog is closed
     */
    declineToS?: () => void;
}

export interface ICaptchaProps {
    recaptcha2PublicKey?: string;
    recaptcha3PublicKey?: string;
    disableRecaptcha3?: boolean;
}

export interface IRequestProps {
    stld?: string;
    sessionId?: string;
}

export interface IObjectKeys {
    [key: string]: any;
}
export interface ISSOSettingsProps extends IObjectKeys {
    twoStepsSignup?: boolean;
    google?: {
        scope: string;
        redirectURI: string;
        clientId: string;
        skipScriptLoading?: boolean;
    };
    facebook?: {
        scope: string;
        version: string;
        appId: string;
        skipScriptLoading?: boolean;
    };
    apple?: {
        scope: string;
        redirectURI: string;
        clientId: string;
        skipScriptLoading?: boolean;
    };
}

export enum DisabledSocialOptions {
    facebook = 'facebook',
    apple = 'apple',
    google = 'google',
    googleOneTap = 'googleOneTap',
}

export type DisabledSocialOptionsValues =
    | 'facebook'
    | 'apple'
    | 'google'
    | 'googleOneTap';

export interface ISocialConfigProps {
    SSOSettings?: ISSOSettingsProps;
    skipSocialCalls?: boolean;
    socialDataMock?: any;
    disabledOptions?: DisabledSocialOptionsValues[];
    secondarySocialMethods?: { [x: string]: boolean };
}

export interface IHeaderProps {
    titleText?: GenericLazyString;
    subtitleText?: GenericLazyString;
    headerLinkProps?: {
        onHeaderClick: () => void;
        linkTo: string;
        linkText: GenericLazyString;
        linkTestid?: string;
    };
}

/**  OTHER  **/
export enum DisabledComponentsTypes {
    header = 'header',
    notifications = 'notifications',
    // forgotPassword = 'forgotPassword',
    // signup = 'signup',
    // social = 'social',
}

export enum DisabledFunctionsTypes {
    lookup = 'lookup',
    // login = 'login',
    // signup = 'signup',
    // passwordless = 'passwordless',
}

export interface INotificationConfigProps {
    /**
     * Shows an extra message following the default notification message
     * extraMessage allows for html to be inside the gettext
     * string to ensure you can add some styles.
     * Use with caution!
     */
    extraMessage?: GenericLazyString | string;
    /**
     * Triggered when the notification action is clicked
     * for non existent users.
     * If this function is not passed, no action is available
     * for the user
     */
    ctaAction?: () => void;
    ctaMessage?: GenericLazyString | string;
}
